import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./ButtonChoice.styles";
import { RadioIcon } from "./RadioIcon";
import { CheckBoxIcon } from "./CheckBoxIcon";
import { VariableModel } from "../../models/VariableModel";
import useQuestion from "../../hooks/useQuestion";
import {
    getAriaLabelledByFromId,
    useAriaLabelledByForQuestionOpenEndChoiceBox,
} from "../../utils/WCAGHelper";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import { ButtonChoiceNavigation } from "../controls/KeyboardNavigation/ButtonChoiceNavigation";
import { QuestionType } from "../../models/enums/QuestionType";

interface Props {
    id?: string;
    choiceElementId?: string;
    type: "checkbox" | "radio";
    label?: string;
    className?: string;
    disabled?: boolean;
    selected?: boolean;
    hasOpenEnd?: boolean;
    openEndSize?: string;
    horizontal?: boolean;
    onSelect?(): void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    renderChoice?(): any;
    renderAfter?(): any;
    carrouselChoiceBackgound?: string;
    variable?: VariableModel;
}

export function ButtonChoice(props: Props) {
    const {
        id,
        choiceElementId,
        disabled,
        label,
        type,
        className,
        hasOpenEnd,
        openEndSize,
        horizontal,
        selected,
        onSelect,
        renderChoice,
        renderAfter,
        carrouselChoiceBackgound,
        variable,
    } = props;

    const question = useQuestion();
    const [hover, setHover] = React.useState(false);
    const [active, setActive] = React.useState(false);
    const useAriaLabelledBy = useAriaLabelledByForQuestionOpenEndChoiceBox(question);

    const theme = useTheme() as any;
    const classes = useStyles({
        theme,
        selected,
        hasOpenEnd,
        openEndSize,
        horizontal,
        carrouselChoiceBackgound,
        type,
    });

    const ChoiceIcon = type === "radio" ? RadioIcon : CheckBoxIcon;

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        id,
        variable,
        question,
        ButtonChoiceNavigation,
        onSelect,
    );

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            id={id}
            role={type}
            ref={elementReference}
            aria-checked={selected}
            {...(useAriaLabelledBy
                ? { "aria-labelledby": getAriaLabelledByFromId(question, choiceElementId) }
                : { "aria-label": label })}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            onKeyDown={onKeyDown}
            onMouseDown={(e) => {
                const div = e.target as HTMLDivElement;
                const activeElement = document.activeElement as HTMLElement;
                if (!div.contains(activeElement)) activeElement.blur();
                e.preventDefault();
            }}
            onClick={() => {
                onSelect();
                if (question.type != QuestionType.Carrousel || type != "radio") {
                    document.getElementById(id).focus();
                }
            }}
            className={classnames(
                classes.choice,
                ...([className] || []),
                hover && classes.choiceHover,
                active && classes.choiceActive,
                selected && classes.choiceSelected,
            )}
        >
            <div className={classes.choiceContainer}>
                {renderChoice ? (
                    renderChoice()
                ) : (
                    <>
                        <ChoiceIcon checked={selected} disabled={disabled} hover={hover} />
                        {theme.DisplayChoiceIcon !== "none" && (
                            <span className={classes.spacer} aria-hidden="true" />
                        )}
                        <span
                            className={classes.choiceText}
                            id={id}
                            dangerouslySetInnerHTML={{ __html: label }}
                        />
                    </>
                )}
            </div>
            {renderAfter && renderAfter()}
        </div>
    );
}
